import { fetchAuthSession } from "@aws-amplify/core";
import { LambdaClient, InvokeCommand, InvokeCommandOutput } from "@aws-sdk/client-lambda";
import { Buffer } from "buffer";

import {
	AnalyticsEvent,
	AnalyticsConfig,
	AnalyticsResponse,
	EventType,
	AnalyticsError,
	NetworkError,
	ValidationError,
	generateEventId,
	calculateTTL,
	createTimestamp,
	DEFAULT_ANALYTICS_CONFIG,
} from "../../types/analytics";
import { CreateEventInput, validateEvent, AnalyticsCategory, SystemAction } from "../../types/analyticsStandards";
import { detectDeviceType, getDeviceMetadata } from "../../utils/deviceDetection";
import { db } from "../../db/db";

/**
 * Core Analytics Service for FactorEarth Record Application
 * Handles event creation, validation, and submission to Lambda functions
 */
export class AnalyticsService {
	private config: AnalyticsConfig;
	private currentUserId: string | null = null;
	private currentGroupId: string | null = null;
	private lambdaClient: LambdaClient | null = null;
	// Simple session management
	private currentSession: { sessionId: string; startTime: string } | null = null;
	// Sync lock to prevent concurrent sync operations
	private isSyncInProgress: boolean = false;

	constructor(config: Partial<AnalyticsConfig> = {}) {
		this.config = { ...DEFAULT_ANALYTICS_CONFIG, ...config };
	}

	/**
	 * Initialize the analytics service with user context
	 * SIMPLIFIED: Just set user context, no complex checks
	 */
	async initialize(userId: string, groupId: string): Promise<void> {
		this.currentUserId = userId;
		this.currentGroupId = groupId;

		try {
			// Initialize Lambda client for sync
			await this.initializeLambdaClient();
		} catch (error) {
			console.error("Failed to initialize Lambda client (will retry on sync):", error);
			// Don't throw - analytics should continue working offline
		}
	}

	/**
	 * Track an analytics event - SIMPLIFIED!
	 * Just store to IndexedDB, let sync system handle the cloud upload
	 */
	async trackEvent(input: CreateEventInput): Promise<void> {
		try {
			// Create session if needed (simplified auto-creation)
			if (!this.currentSession) {
				this.createSession();
			}

			// Validate event structure
			const validation = validateEvent(input);
			if (!validation.isValid) {
				console.error("Invalid event:", validation.errors);
				return; // Skip invalid events but don't throw
			}

			// Create and store event
			const event = this.createEvent(input);

			// Always store to IndexedDB - sync system handles cloud upload
			await this.storeEventOffline(event);
		} catch (error) {
			console.error("Failed to store analytics event:", error);
			// Don't throw - analytics failures shouldn't break the app
		}
	}

	/**
	 * Simple auto-session creation with user-specific persistence
	 */
	private createSession(): void {
		if (!this.currentUserId) return;

		// Use same session key pattern as RootContainer
		const sessionKey = `analytics_session_${this.currentUserId}`;
		const sessionIdKey = `analytics_sessionId_${this.currentUserId}`;

		// Check if we should reuse existing session (within 30 minutes)
		const lastSessionStart = sessionStorage.getItem(sessionKey);
		const existingSessionId = sessionStorage.getItem(sessionIdKey);
		const now = Date.now();
		const sessionTimeout = 30 * 60 * 1000; // 30 minutes in milliseconds

		let sessionId: string;
		let startTime: string;

		if (lastSessionStart && existingSessionId && now - parseInt(lastSessionStart) < sessionTimeout) {
			// Reuse existing session
			sessionId = existingSessionId;
			startTime = new Date(parseInt(lastSessionStart)).toISOString();
		} else {
			// Create new session
			sessionId = generateEventId();
			startTime = createTimestamp();

			// Store both timestamp and session ID
			sessionStorage.setItem(sessionKey, now.toString());
			sessionStorage.setItem(sessionIdKey, sessionId);
		}

		this.currentSession = {
			sessionId,
			startTime,
		};
	}

	/**
	 * Get current session info with user-specific persistence
	 */
	getSessionInfo(): { sessionId: string; startTime: string } | null {
		// Try to recover from sessionStorage if session lost
		if (!this.currentSession && this.currentUserId) {
			const sessionKey = `analytics_session_${this.currentUserId}`;
			const sessionIdKey = `analytics_sessionId_${this.currentUserId}`;

			const lastSessionStart = sessionStorage.getItem(sessionKey);
			const existingSessionId = sessionStorage.getItem(sessionIdKey);
			const now = Date.now();
			const sessionTimeout = 30 * 60 * 1000; // 30 minutes

			if (lastSessionStart && existingSessionId && now - parseInt(lastSessionStart) < sessionTimeout) {
				// Recover existing session
				this.currentSession = {
					sessionId: existingSessionId,
					startTime: new Date(parseInt(lastSessionStart)).toISOString(),
				};
			}
		}

		return this.currentSession;
	}

	/**
	 * Store event to IndexedDB for offline-first approach
	 */
	private async storeEventOffline(event: AnalyticsEvent): Promise<void> {
		try {
			// Convert AnalyticsEvent to IndexedDB format
			const analyticsEventData = {
				id: event.id,
				eventType: event.eventType,
				userId: event.userId,
				groupId: event.groupId,
				timestamp: event.timestamp,
				sessionId: event.sessionId,
				deviceType: event.deviceType,
				metadata: event.metadata || {},
				appVersion: event.appVersion,
				offlineQueued: 1, // Needs sync
				createdAt: event.clientTimestamp || event.timestamp,
				updatedAt: event.clientTimestamp || event.timestamp,
			};

			await db.analyticsEvent.add(analyticsEventData);
		} catch (error) {
			console.error("Failed to store event to IndexedDB:", error);
			throw new AnalyticsError(`Failed to store event offline: ${error instanceof Error ? error.message : String(error)}`);
		}
	}

	/**
	 * Simple batch sync - send all pending events to Lambda
	 */
	async syncPendingEvents(): Promise<{ synced: number; failed: number }> {
		if (!this.lambdaClient) {
			console.error("Lambda client not initialized");
			throw new Error("Lambda client not initialized");
		}

		// Prevent concurrent sync operations
		if (this.isSyncInProgress) {
			return { synced: 0, failed: 0 };
		}

		this.isSyncInProgress = true;

		try {
			// Get all pending events
			const pendingEvents = await db.analyticsEvent.where("offlineQueued").equals(1).toArray();

			if (pendingEvents.length === 0) {
				return { synced: 0, failed: 0 };
			}

			// Convert to Lambda format
			const events = pendingEvents.map((eventData) => ({
				eventId: eventData.id,
				eventType: eventData.eventType,
				userId: eventData.userId,
				groupId: eventData.groupId,
				timestamp: eventData.timestamp,
				sessionId: eventData.sessionId,
				deviceType: eventData.deviceType,
				metadata: eventData.metadata,
				appVersion: eventData.appVersion,
				clientTimestamp: eventData.createdAt,
				offlineQueued: true,
				ttl: calculateTTL(30),
			}));

			// Send batch to Lambda
			await this.sendEventBatch(events);

			// Mark all as synced (simple approach - if batch succeeds, all succeed)
			await db.analyticsEvent
				.where("id")
				.anyOf(pendingEvents.map((e) => e.id))
				.modify({ offlineQueued: 0 });

			// Immediately cleanup all synced events - no need to keep them locally
			const cleanup = await this.cleanupSyncedEventsImmediately();

			return { synced: pendingEvents.length, failed: 0 };
		} catch (error) {
			console.error("Batch sync failed:", error);
			if (this.config.debug) {
				console.error("Debug mode - full error:", error);
			}
			// Simple error handling - if batch fails, all fail
			return { synced: 0, failed: 1 };
		} finally {
			// Always release the sync lock
			this.isSyncInProgress = false;
		}
	}

	/**
	 * Clean up old synced events from IndexedDB
	 * Removes synced events older than specified days (default: 2 days)
	 */
	async cleanupOldEvents(retentionDays: number = 2): Promise<{ deleted: number }> {
		try {
			const cutoffDate = new Date();
			cutoffDate.setDate(cutoffDate.getDate() - retentionDays);
			const cutoffTimestamp = cutoffDate.toISOString();

			// Only delete synced events (offlineQueued: 0) older than retention period
			const oldSyncedEvents = await db.analyticsEvent
				.where("offlineQueued")
				.equals(0)
				.and((event) => event.createdAt < cutoffTimestamp)
				.toArray();

			if (oldSyncedEvents.length === 0) {
				return { deleted: 0 };
			}

			// Delete old synced events
			await db.analyticsEvent
				.where("id")
				.anyOf(oldSyncedEvents.map((e) => e.id))
				.delete();

			return { deleted: oldSyncedEvents.length };
		} catch (error) {
			console.error("Failed to cleanup old events:", error);
			return { deleted: 0 };
		}
	}

	/**
	 * Clean up synced events immediately after successful sync
	 * More aggressive cleanup - removes all synced events immediately
	 */
	async cleanupSyncedEventsImmediately(): Promise<{ deleted: number }> {
		try {
			// Delete all synced events (offlineQueued: 0) regardless of age
			const syncedEvents = await db.analyticsEvent.where("offlineQueued").equals(0).toArray();

			if (syncedEvents.length === 0) {
				return { deleted: 0 };
			}

			await db.analyticsEvent
				.where("id")
				.anyOf(syncedEvents.map((e) => e.id))
				.delete();

			return { deleted: syncedEvents.length };
		} catch (error) {
			console.error("Failed to cleanup synced events immediately:", error);
			return { deleted: 0 };
		}
	}

	/**
	 * Get analytics database statistics
	 */
	async getAnalyticsStats(): Promise<{
		totalEvents: number;
		pendingEvents: number;
		syncedEvents: number;
	}> {
		try {
			const [total, pending, synced] = await Promise.all([
				db.analyticsEvent.count(),
				db.analyticsEvent.where("offlineQueued").equals(1).count(),
				db.analyticsEvent.where("offlineQueued").equals(0).count(),
			]);

			return {
				totalEvents: total,
				pendingEvents: pending,
				syncedEvents: synced,
			};
		} catch (error) {
			console.error("Failed to get analytics stats:", error);
			return {
				totalEvents: 0,
				pendingEvents: 0,
				syncedEvents: 0,
			};
		}
	}

	/**
	 * Send heartbeat to keep session alive
	 */
	async sendHeartbeat(): Promise<void> {
		// DEPRECATED: Heartbeats removed in favor of event-based session tracking
		// This method exists for backward compatibility but does nothing
		if (this.config.debug) {
			console.warn("sendHeartbeat() called but heartbeats are deprecated. Use actual user events for session tracking.");
		}
		return;
	}

	// Convenience methods for specific event types

	/**
	 * Track form start event
	 */
	async trackFormStart(formId: string, metadata: Record<string, any> = {}): Promise<void> {
		return this.trackEvent({
			category: "form_builder",
			action: "form_start",
			metadata: {
				formId,
				...metadata,
			},
		});
	}

	/**
	 * Track project creation event
	 */
	async trackProjectCreate(projectId: string, metadata: Record<string, any> = {}): Promise<void> {
		return this.trackEvent({
			category: "project_management",
			action: "create",
			metadata: {
				projectId,
				...metadata,
			},
		});
	}

	/**
	 * Track project edit event
	 */
	async trackProjectEdit(projectId: string, metadata: Record<string, any> = {}): Promise<void> {
		return this.trackEvent({
			category: "project_management",
			action: "update",
			metadata: {
				projectId,
				...metadata,
			},
		});
	}

	/**
	 * Track template creation event
	 */
	async trackTemplateCreate(templateId: string, metadata: Record<string, any> = {}): Promise<void> {
		return this.trackEvent({
			category: "form_builder",
			action: "create",
			metadata: {
				templateId,
				...metadata,
			},
		});
	}

	/**
	 * Track template edit event
	 */
	async trackTemplateEdit(templateId: string, metadata: Record<string, any> = {}): Promise<void> {
		return this.trackEvent({
			category: "form_builder",
			action: "update",
			metadata: {
				templateId,
				...metadata,
			},
		});
	}

	/**
	 * Track template deletion event
	 */
	async trackTemplateDelete(templateId: string, metadata: Record<string, any> = {}): Promise<void> {
		return this.trackEvent({
			category: "form_builder",
			action: "delete",
			metadata: {
				templateId,
				...metadata,
			},
		});
	}

	/**
	 * Track export event
	 */
	async trackExport(exportType: string, metadata: Record<string, any> = {}): Promise<void> {
		return this.trackEvent({
			category: "reporting",
			action: "export",
			metadata: {
				exportType,
				...metadata,
			},
		});
	}

	/**
	 * Track export start event
	 */
	async trackExportStart(exportType: string, metadata: Record<string, any> = {}): Promise<void> {
		return this.trackEvent({
			category: "reporting",
			action: "export_start",
			metadata: {
				exportType,
				...metadata,
			},
		});
	}

	/**
	 * Track export success event
	 */
	async trackExportSuccess(exportType: string, metadata: Record<string, any> = {}): Promise<void> {
		return this.trackEvent({
			category: "reporting",
			action: "export_success",
			metadata: {
				exportType,
				...metadata,
			},
		});
	}

	/**
	 * Track export failure event
	 */
	async trackExportFailure(exportType: string, error: string, metadata: Record<string, any> = {}): Promise<void> {
		return this.trackEvent({
			category: "reporting",
			action: "export_failure",
			metadata: {
				exportType,
				error,
				...metadata,
			},
		});
	}

	/**
	 * Check if analytics is enabled
	 */
	isEnabled(): boolean {
		return this.config.enabled;
	}

	/**
	 * Update user context
	 * @param userId User document ID (user.id)
	 * @param groupId User's group/organization (user.group)
	 */
	updateUserContext(userId: string, groupId: string): void {
		this.currentUserId = userId;
		this.currentGroupId = groupId;
	}

	// Private methods

	/**
	 * Create an analytics event from input - simplified
	 */
	private createEvent(input: CreateEventInput): AnalyticsEvent {
		if (!this.currentUserId) {
			throw new ValidationError("User ID is required for analytics events");
		}

		if (!this.currentGroupId) {
			throw new ValidationError("Group ID is required for analytics events");
		}

		// Get or create session
		let session = this.getSessionInfo();
		if (!session) {
			this.createSession();
			session = this.getSessionInfo();
		}

		if (!session) {
			throw new ValidationError("Failed to create session for analytics events");
		}

		// Map new standardized format to legacy EventType for backward compatibility
		let legacyEventType = this.mapToLegacyEventType(input.category, input.action);

		return {
			id: generateEventId(),
			// Legacy field for backward compatibility
			eventType: legacyEventType,
			// New standardized fields
			category: input.category,
			action: input.action,
			label: input.label,
			value: input.value,
			// Core fields
			userId: this.currentUserId,
			groupId: this.currentGroupId,
			timestamp: createTimestamp(),
			clientTimestamp: createTimestamp(),
			sessionId: session.sessionId,
			deviceType: detectDeviceType(),
			metadata: input.metadata || {},
			appVersion: import.meta.env.VITE_APP_VERSION || "1.0.0",
			offlineQueued: true,
			ttl: calculateTTL(30), // 30 days
		};
	}

	/**
	 * Map new standardized action/category to legacy EventType for backward compatibility
	 */
	private mapToLegacyEventType(category: string, action: string): EventType {
		// Map common patterns to existing EventTypes
		if (category === AnalyticsCategory.PROJECT_MANAGEMENT) {
			if (action.includes("create")) return EventType.PROJECT_CREATE;
			if (action.includes("edit") || action.includes("update")) return EventType.PROJECT_EDIT;
		}

		if (category === AnalyticsCategory.FORM_BUILDER) {
			if (action.includes("create")) return EventType.TEMPLATE_CREATE;
			if (action.includes("edit") || action.includes("update")) return EventType.TEMPLATE_EDIT;
			if (action.includes("delete")) return EventType.TEMPLATE_DELETE;
		}

		if (category === AnalyticsCategory.CONTENT && action.includes("export")) {
			return EventType.EXPORT;
		}

		if (category === AnalyticsCategory.SYSTEM) {
			if (action === SystemAction.SESSION_START) return EventType.SESSION_START;
			if (action === SystemAction.SESSION_END) return EventType.SESSION_END;
		}

		if (category === AnalyticsCategory.NAVIGATION) {
			// Navigation events
			if (action === "page_view") return EventType.PAGE_VIEW;
			if (action === "refresh") return EventType.REFRESH;
			if (action === "route_change") return EventType.ROUTE_CHANGE;
			if (action === "menu_click") return EventType.MENU_CLICK;
		}

		if (category === AnalyticsCategory.USER_BEHAVIOR) {
			// Session events
			if (action === "session_start") return EventType.SESSION_START;
			if (action === "session_end") return EventType.SESSION_END;
			// Note: session_heartbeat removed - we use actual user events for session tracking

			// Form events
			if (action === "form_create") return EventType.FORM_CREATE;
			if (action === "form_group_create") return EventType.FORM_GROUP_CREATE;
			if (action === "form_group_edit") return EventType.FORM_GROUP_EDIT;
			if (action === "field_edit") return EventType.FIELD_EDIT;

			// Navigation events
			if (action === "route_change") return EventType.ROUTE_CHANGE;

			// Project events
			if (action === "project_create") return EventType.PROJECT_CREATE;
			if (action === "project_edit") return EventType.PROJECT_EDIT;

			// Export events
			if (action === "data_export") return EventType.DATA_EXPORT;

			// History events
			if (action === "history_access") return EventType.HISTORY_ACCESS;
			if (action === "history_revert") return EventType.HISTORY_REVERT;
		}

		// Default fallback - log warning and use a generic event type
		if (this.config.debug) {
			console.warn(`Unmapped event: category=${category}, action=${action}. Using PAGE_VIEW as fallback.`);
		}
		return EventType.PAGE_VIEW; // Generic fallback instead of SESSION_HEARTBEAT
	}

	/**
	 * Send batch of events to Lambda function - simplified
	 */
	private async sendEventBatch(events: any[]): Promise<void> {
		if (!this.lambdaClient) {
			console.error("Lambda client not initialized in sendEventBatch");
			throw new NetworkError("Lambda client not initialized");
		}

		// Format for Lambda function that expects API Gateway format
		const lambdaEvent = {
			httpMethod: "POST",
			body: JSON.stringify({ events }),
			headers: {
				"Content-Type": "application/json",
			},
		};

		const functionName = `AnalyticsIngest-${import.meta.env.VITE_APP_ENV !== "production" ? "staging" : "production"}`;

		const command = new InvokeCommand({
			FunctionName: functionName,
			Payload: Buffer.from(JSON.stringify(lambdaEvent)),
		});

		try {
			const response: InvokeCommandOutput = await this.lambdaClient.send(command);

			if (response.StatusCode !== 200) {
				console.error("Lambda invocation failed with status:", response.StatusCode);
				throw new NetworkError(`Lambda invocation failed with status: ${response.StatusCode}`);
			}

			if (response.Payload) {
				const rawResponse = JSON.parse(new TextDecoder().decode(response.Payload));

				// Handle API Gateway response format
				let responsePayload: AnalyticsResponse;
				if (rawResponse.statusCode && rawResponse.body) {
					responsePayload = JSON.parse(rawResponse.body);
				} else {
					responsePayload = rawResponse;
				}

				if (!responsePayload.success) {
					throw new NetworkError("Analytics batch processing failed");
				}
			} else {
				console.error("No response payload from Lambda");
				throw new NetworkError("No response payload from Lambda");
			}
		} catch (error) {
			console.error("Failed to send analytics batch:", error);
			if (this.config.debug) {
				console.error("Debug mode - sendEventBatch full error:", error);
			}
			throw new NetworkError(`Failed to send analytics batch: ${error instanceof Error ? error.message : "Unknown error"}`);
		}
	}

	/**
	 * Initialize Lambda client with credentials
	 */
	private async initializeLambdaClient(): Promise<void> {
		try {
			const { credentials } = await fetchAuthSession();

			this.lambdaClient = new LambdaClient({
				region: "us-west-1", // Match your Lambda region
				credentials,
			});
		} catch (error) {
			throw new NetworkError("Failed to initialize Lambda client for analytics");
		}
	}
}

// Export singleton instance
export const analyticsService = new AnalyticsService();
